import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, FormField } from '@/components';
import { verifyOtp, sendOtp } from '../../api';
import {
  AUTH_BUTTON_CLASS,
  AUTH_FORM_CARD_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
} from '../../lib/authStyles';

export function VerifyOtpForm() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const currentFlow = location.state?.flow || 'verification';
    const isCompleted =
      sessionStorage.getItem('reset_flow_completed') === 'true';
    if (!location.state || (isCompleted && currentFlow === 'verification')) {
      navigate('/login', { replace: true });
    }
  }, [location.state, navigate]);

  // Try to read parameters passed from the registration page
  const target =
    location.state?.email || location.state?.mobile || 'your email/mobile';
  const flow = location.state?.flow || 'verification';
  const { expires_in_seconds } = location.state || {};

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(expires_in_seconds || 60);
  const [fieldErrors, setFieldErrors] = useState({});

  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];

  // Countdown timer logic
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const { mutate: doVerify, isPending: isVerifying } = useMutation({
    mutationFn: verifyOtp,
    onSuccess: (data) => {
      if (flow === 'verification') {
        toast.success(data?.message || 'OTP verified successfully!');
        navigate('/reset-password', {
          replace: true,
          state: {
            email: target,
            otp_code: otp.join(''),
          },
        });
        return;
      }

      if (data?.tokens) {
        localStorage.setItem('access_token', data.tokens.access);
        localStorage.setItem('refresh_token', data.tokens.refresh);
      }
      if (data?.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.user.company_id) {
          localStorage.setItem('company_id', data.user.company_id);
        }
        if (data.user.company_name) {
          localStorage.setItem('company_name', data.user.company_name);
        }
      }
      toast.success(data?.message || 'Verification successful!');
      setTimeout(() => {
        navigate('/dashboard', { replace: true }); // Redirect to dashboard after successful login/verification
      }, 1500);
    },
    onError: (err) => {
      const errorData = err.response?.data;
      if (
        errorData &&
        typeof errorData === 'object' &&
        !Array.isArray(errorData) &&
        !errorData.message &&
        !errorData.detail
      ) {
        const newErrors = {};
        Object.keys(errorData).forEach((key) => {
          let fieldName = key;
          if (key === 'otp_code') fieldName = 'otp';

          const errMsg = Array.isArray(errorData[key])
            ? errorData[key][0]
            : errorData[key];

          if (key === 'non_field_errors') {
            toast.error(errMsg);
          } else {
            newErrors[fieldName] = errMsg;
          }
        });
        setFieldErrors(newErrors);
      } else if (Array.isArray(errorData) && errorData.length > 0) {
        toast.error(errorData[0]);
      } else {
        toast.error(
          errorData?.message ||
            errorData?.detail ||
            'Invalid OTP code. Please try again.',
        );
      }
    },
  });

  const { mutate: doResend, isPending: isResending } = useMutation({
    mutationFn: sendOtp,
    onSuccess: (data) => {
      toast.success(data?.message || 'A new OTP has been sent successfully.');
      setTimer(data?.expires_in_seconds || 60);
    },
    onError: (err) => {
      const errorData = err.response?.data;
      if (Array.isArray(errorData) && errorData.length > 0) {
        toast.error(errorData[0]);
      } else {
        toast.error(
          errorData?.message ||
            errorData?.detail ||
            'Failed to resend OTP. Please try again.',
        );
      }
    },
  });

  const handleOtpChange = (index, value) => {
    if (fieldErrors.otp) setFieldErrors({ ...fieldErrors, otp: '' });
    if (isNaN(Number(value))) return; // Only allow numbers

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1); // Only keep last digit
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs[index + 1].current.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if empty
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs[index - 1].current.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    if (!/^\d+$/.test(pastedData)) return; // Only numeric codes

    const pastedDigits = pastedData.slice(0, 6).split('');
    const newOtp = [...otp];
    pastedDigits.forEach((digit, index) => {
      newOtp[index] = digit;
      if (inputRefs[index]?.current) {
        inputRefs[index].current.value = digit;
      }
    });
    setOtp(newOtp);

    // Focus last filled box
    const focusIndex = Math.min(pastedDigits.length, 5);
    inputRefs[focusIndex].current.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const code = otp.join('');

    if (code.length < 6) {
      setFieldErrors({ otp: 'Please enter all 6 digits.' });
      return;
    }

    doVerify({ target, code, flow });
  };

  const handleResend = () => {
    if (timer > 0 || isResending) return;
    doResend({ target, flow });
  };

  if (!location.state) return null;

  return (
    <div className={`${AUTH_FORM_CARD_CLASS} text-left`}>
      <form onSubmit={handleSubmit} className="w-full flex flex-col">
        <FormField
          label="Enter OTP"
          error={fieldErrors.otp}
          className="mb-6"
          labelClassName={AUTH_LABEL_CLASS}
        >
          {/* Digit Input Boxes */}
          <div className="grid grid-cols-6 gap-2 sm:gap-3 md:gap-4 w-full max-w-[710px]">
            {otp.map((digit, idx) => (
              <input
                key={idx}
                ref={inputRefs[idx]}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                placeholder="-"
                value={digit}
                onChange={(e) => handleOtpChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                onPaste={handlePaste}
                className={`w-full aspect-square sm:aspect-[9/7] max-w-[90px] text-center font-semibold font-sf-pro-display rounded-input bg-input-background border border-border-default text-text-primary focus:outline-none focus:border-border-active transition-all px-0 ${AUTH_INPUT_CLASS}`}
                disabled={isVerifying}
              />
            ))}
          </div>
        </FormField>

        {/* Resend Link */}
        <div className="text-center mb-6">
          <span className="text-body-lg font-normal font-sf-pro-display text-text-primary">
            Didn&apos;t receive the code?{' '}
          </span>
          {timer > 0 ? (
            <span className="text-body-lg font-normal font-sf-pro-display text-text-primary">
              Resend OTP in {timer}s
            </span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              className="text-body-lg font-normal font-sf-pro-display text-primary underline hover:text-primary-hover focus:outline-none disabled:opacity-50"
              disabled={isResending}
            >
              {isResending ? 'Resending...' : 'Resend OTP'}
            </button>
          )}
        </div>

        {/* Submit Verification Button */}
        <div className="flex justify-center mb-6">
          <Button
            size="lg"
            type="submit"
            variant="primary"
            className={`${AUTH_BUTTON_CLASS} w-[180px] h-[48px]`}
            disabled={isVerifying}
          >
            {isVerifying ? (
              <span className="flex items-center justify-center w-full">
                <svg
                  className="animate-spin h-5 w-5 text-surface-mint"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </span>
            ) : (
              <>
                <span>Verify OTP</span>
                <span>→</span>
              </>
            )}
          </Button>
        </div>

        {/* Already have an account / Back to Login */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              localStorage.clear();
              window.location.href = '/login';
            }}
            className="text-body-lg font-normal font-sf-pro-display text-text-primary hover:text-primary transition-colors cursor-pointer inline-flex items-center gap-1.5"
          >
            <svg
              className="w-4 h-4 inline"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
            <span>Back to Login</span>
          </button>
        </div>
      </form>
    </div>
  );
}
